/* eslint-disable */
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box,
  TextField,
  Button,
  IconButton,
  Stack,
  Typography,
  CircularProgress,
  useTheme,
  Alert,
  Paper,
  Checkbox,
  FormControlLabel,
  Divider,
} from "@mui/material";
import { Close as CloseIcon, VpnKey as VpnKeyIcon } from "@mui/icons-material";
import { assignRolePermissions } from "store/features/admin/roleManagement/roleAccess/roleAccessActions";
import { selectPermissions } from "store/features/admin/roleManagement/permission/permissionSelectors";
import { fetchPermissions } from "store/features/admin/roleManagement/permission/permissionActions";
import { selectRoles } from "store/features/admin/roleManagement/role/roleSelectors";
import { fetchRoles } from "store/features/admin/roleManagement/role/roleActions";
import { selectRolePermissions } from "store/features/admin/roleManagement/roleAccess/roleAccessSelectors";
import addRoleAccessSvg from "assets/modals/add.svg";

const AddRoleAccessModal = ({ isOpen, onClose, onSuccess, selectedRole }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const allPermissions = useSelector(selectPermissions);
  const roles = useSelector(selectRoles);
  const existingRolePermissions = useSelector(selectRolePermissions);

  const [loading, setLoading] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [selectedPermissionIds, setSelectedPermissionIds] = useState([]);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      dispatch(fetchPermissions());
      dispatch(fetchRoles());
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen, dispatch]);

  // Set selected role when editing
  useEffect(() => {
    if (selectedRole && isOpen) {
      setSelectedRoleId(selectedRole.role_id.toString());
      // Get existing permissions for this role
      const existingPermissions = existingRolePermissions.filter(
        (p) => p.role_id === selectedRole.role_id,
      );
      setSelectedPermissionIds(existingPermissions.map((p) => p.permission_id));
    } else if (!selectedRole && isOpen) {
      setSelectedRoleId("");
      setSelectedPermissionIds([]);
    }
  }, [selectedRole, isOpen, existingRolePermissions]);

  // Group permissions by resource
  const permissionsByResource = allPermissions.reduce((grouped, permission) => {
    const resource = permission.resource;
    if (!grouped[resource]) {
      grouped[resource] = [];
    }
    grouped[resource].push(permission);
    return grouped;
  }, {});

  const validateForm = () => {
    const newErrors = {};
    if (!selectedRoleId) newErrors.role = "Please select a role";
    if (selectedPermissionIds.length === 0)
      newErrors.permissions = "Please select at least one permission";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePermissionToggle = (permissionId) => {
    setSelectedPermissionIds((prev) =>
      prev.includes(permissionId)
        ? prev.filter((id) => id !== permissionId)
        : [...prev, permissionId],
    );
    if (errors.permissions) {
      setErrors((prev) => ({ ...prev, permissions: "" }));
    }
  };

  const handleSelectAllResource = (resourcePermissions, isChecked) => {
    const resourcePermissionIds = resourcePermissions.map((p) => p.id);
    if (isChecked) {
      const newIds = [...selectedPermissionIds];
      resourcePermissionIds.forEach((id) => {
        if (!newIds.includes(id)) newIds.push(id);
      });
      setSelectedPermissionIds(newIds);
    } else {
      setSelectedPermissionIds(
        selectedPermissionIds.filter(
          (id) => !resourcePermissionIds.includes(id),
        ),
      );
    }
  };

  const handleSelectAll = () => {
    if (selectedPermissionIds.length === allPermissions.length) {
      setSelectedPermissionIds([]);
    } else {
      setSelectedPermissionIds(allPermissions.map((p) => p.id));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    const formattedData = {
      role_id: parseInt(selectedRoleId),
      permissionIds: selectedPermissionIds,
    };

    const result = await dispatch(assignRolePermissions(formattedData));

    if (result.success) {
      onSuccess();
      onClose();
      resetForm();
    }
    setLoading(false);
  };

  const resetForm = () => {
    setSelectedRoleId("");
    setSelectedPermissionIds([]);
    setErrors({});
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
      "& fieldset": {
        borderColor: theme.palette.mode === "dark" ? "#475569" : "#d1d5db",
      },
      "&:hover fieldset": {
        borderColor: "#f59e0b",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#f59e0b",
        borderWidth: "2px",
      },
    },
    "& .MuiInputLabel-root": {
      color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
      position: "relative",
      transform: "none",
      marginBottom: "8px",
      display: "block",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "#f59e0b",
    },
  };

  const isAllSelected = selectedPermissionIds.length === allPermissions.length;
  const isSomeSelected =
    selectedPermissionIds.length > 0 &&
    selectedPermissionIds.length < allPermissions.length;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <style>
            {`
          .MuiPopover-root {
            z-index: 1400 !important;
          }
          .MuiPopper-root {
            z-index: 1400 !important;
          }
        `}
          </style>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 1300,
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(245, 158, 11, 0.1)"
                  : "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(4px)",
            }}
          />

          {/* Modal */}
          <motion.div
            initial={{ x: "100%", opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: "100%", opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            style={{
              position: "fixed",
              right: 0,
              top: 0,
              zIndex: 1301,
              height: "100%",
              width: "100%",
              maxWidth: "700px",
              overflowY: "auto",
              backgroundColor:
                theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
              boxShadow: theme.shadows[24],
            }}
          >
            {/* Header */}
            <Box
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 10,
                borderBottom: `1px solid ${
                  theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
                }`,
                backgroundColor:
                  theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
              }}
            >
              <Box sx={{ p: 3, pr: 7 }}>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <Box flexShrink={0}>
                    <Box
                      component="img"
                      src={addRoleAccessSvg}
                      alt="Assign Permissions"
                      sx={{
                        width: 80,
                        height: 80,
                        objectFit: "contain",
                        transition: "all 0.3s",
                        "&:hover": {
                          filter: "drop-shadow(0 4px 8px rgba(245,158,11,0.3))",
                        },
                      }}
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  </Box>
                  <Box flex={1}>
                    <Typography
                      variant="h5"
                      component="h2"
                      sx={{
                        fontWeight: "bold",
                        color:
                          theme.palette.mode === "dark" ? "#ffffff" : "#111827",
                      }}
                    >
                      {selectedRole
                        ? "Edit Role Permissions"
                        : "Assign Role Permissions"}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 0.5,
                        color:
                          theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                      }}
                    >
                      {selectedRole
                        ? `Modify permissions for ${selectedRole.role_name}`
                        : "Select a role and assign permissions"}
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <IconButton
                onClick={onClose}
                sx={{
                  position: "absolute",
                  top: 20,
                  right: 20,
                  width: 36,
                  height: 36,
                  borderRadius: "8px",
                  "&:hover": { backgroundColor: "#f59e0b", color: "#ffffff" },
                }}
              >
                <CloseIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>

            {/* Form */}
            <form onSubmit={handleSubmit}>
              <Box sx={{ p: 3 }}>
                <Stack spacing={3}>
                  {/* Info Alert */}
                  <Alert
                    severity="info"
                    sx={{
                      borderRadius: "12px",
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(245, 158, 11, 0.1)"
                          : "#fffbef",
                      "& .MuiAlert-icon": {
                        color: "#f59e0b",
                      },
                    }}
                  >
                    <Typography variant="body2">
                      Select the permissions you want to assign to the role.
                      {selectedRole &&
                        ` Currently editing permissions for ${selectedRole.role_name}.`}
                    </Typography>
                  </Alert>

                  {/* Role Selection (hide if editing) */}
                  {!selectedRole && (
                    <TextField
                      select
                      label="Select Role"
                      value={selectedRoleId}
                      onChange={(e) => {
                        setSelectedRoleId(e.target.value);
                        setSelectedPermissionIds([]);
                        if (errors.role) {
                          setErrors((prev) => ({ ...prev, role: "" }));
                        }
                      }}
                      required
                      error={!!errors.role}
                      helperText={errors.role}
                      sx={inputSx}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      SelectProps={{
                        MenuProps: {
                          PaperProps: {
                            sx: {
                              borderRadius: "12px",
                              backgroundColor:
                                theme.palette.mode === "dark"
                                  ? "#1e293b"
                                  : "#ffffff",
                            },
                          },
                        },
                      }}
                    >
                      <MenuItem value="">Select a role</MenuItem>
                      {roles.map((role) => (
                        <MenuItem key={role.id} value={role.id}>
                          {role.name} ({role.code})
                        </MenuItem>
                      ))}
                    </TextField>
                  )}

                  {/* Permissions Section */}
                  <Box>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ mb: 2 }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 600,
                          color:
                            theme.palette.mode === "dark"
                              ? "#ffffff"
                              : "#111827",
                        }}
                      >
                        Permissions
                      </Typography>
                      <Button
                        size="small"
                        onClick={handleSelectAll}
                        sx={{
                          textTransform: "none",
                          color: "#f59e0b",
                          "&:hover": {
                            backgroundColor: "rgba(245,158,11,0.1)",
                          },
                        }}
                      >
                        {isAllSelected ? "Deselect All" : "Select All"}
                      </Button>
                    </Stack>

                    {errors.permissions && (
                      <Typography
                        sx={{
                          color: "#ef4444",
                          fontSize: "0.75rem",
                          mb: 2,
                        }}
                      >
                        {errors.permissions}
                      </Typography>
                    )}

                    <Paper
                      variant="outlined"
                      sx={{
                        borderRadius: "12px",
                        borderColor:
                          theme.palette.mode === "dark" ? "#334155" : "#e5e7eb",
                        maxHeight: "500px",
                        overflow: "auto",
                      }}
                    >
                      {Object.entries(permissionsByResource).map(
                        ([resource, perms]) => {
                          const resourcePermissionIds = perms.map((p) => p.id);
                          const allResourceSelected =
                            resourcePermissionIds.every((id) =>
                              selectedPermissionIds.includes(id),
                            );
                          const someResourceSelected =
                            resourcePermissionIds.some((id) =>
                              selectedPermissionIds.includes(id),
                            );

                          return (
                            <Box key={resource}>
                              <Box
                                sx={{
                                  p: 2,
                                  backgroundColor:
                                    theme.palette.mode === "dark"
                                      ? "rgba(30,41,59,0.5)"
                                      : "#f9fafb",
                                  borderBottom: `1px solid ${
                                    theme.palette.mode === "dark"
                                      ? "#334155"
                                      : "#e5e7eb"
                                  }`,
                                }}
                              >
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={allResourceSelected}
                                      indeterminate={
                                        someResourceSelected &&
                                        !allResourceSelected
                                      }
                                      onChange={(e) =>
                                        handleSelectAllResource(
                                          perms,
                                          e.target.checked,
                                        )
                                      }
                                      sx={{
                                        color: "#f59e0b",
                                        "&.Mui-checked": {
                                          color: "#f59e0b",
                                        },
                                        "&.MuiCheckbox-indeterminate": {
                                          color: "#f59e0b",
                                        },
                                      }}
                                    />
                                  }
                                  label={
                                    <Typography
                                      sx={{
                                        fontWeight: 600,
                                        textTransform: "capitalize",
                                        color:
                                          theme.palette.mode === "dark"
                                            ? "#ffffff"
                                            : "#111827",
                                      }}
                                    >
                                      {resource}
                                    </Typography>
                                  }
                                />
                              </Box>
                              <Box sx={{ p: 2, pl: 4 }}>
                                <Stack spacing={1.5}>
                                  {perms.map((permission) => (
                                    <FormControlLabel
                                      key={permission.id}
                                      control={
                                        <Checkbox
                                          checked={selectedPermissionIds.includes(
                                            permission.id,
                                          )}
                                          onChange={() =>
                                            handlePermissionToggle(
                                              permission.id,
                                            )
                                          }
                                          sx={{
                                            color: "#f59e0b",
                                            "&.Mui-checked": {
                                              color: "#f59e0b",
                                            },
                                          }}
                                        />
                                      }
                                      label={
                                        <Stack
                                          direction="row"
                                          spacing={1}
                                          alignItems="center"
                                        >
                                          <Typography
                                            sx={{
                                              fontSize: "0.875rem",
                                              fontWeight: 500,
                                              textTransform: "capitalize",
                                              color:
                                                theme.palette.mode === "dark"
                                                  ? "#d1d5db"
                                                  : "#4b5563",
                                            }}
                                          >
                                            {permission.action}
                                          </Typography>
                                          <Typography
                                            sx={{
                                              fontSize: "0.75rem",
                                              color:
                                                theme.palette.mode === "dark"
                                                  ? "#9ca3af"
                                                  : "#6b7280",
                                            }}
                                          >
                                            - {permission.description}
                                          </Typography>
                                        </Stack>
                                      }
                                    />
                                  ))}
                                </Stack>
                              </Box>
                              <Divider />
                            </Box>
                          );
                        },
                      )}
                    </Paper>
                  </Box>
                </Stack>
              </Box>

              {/* Footer Buttons */}
              <Box
                sx={{
                  p: 3,
                  borderTop: `1px solid ${
                    theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
                  }`,
                  display: "flex",
                  gap: 2,
                }}
              >
                <Button
                  onClick={onClose}
                  disabled={loading}
                  fullWidth
                  variant="outlined"
                  sx={{
                    borderRadius: "12px",
                    py: 1.2,
                    textTransform: "none",
                    fontWeight: 500,
                    borderColor:
                      theme.palette.mode === "dark" ? "#475569" : "#d1d5db",
                    color:
                      theme.palette.mode === "dark" ? "#d1d5db" : "#374151",
                    "&:hover": {
                      borderColor: "#f59e0b",
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(245,158,11,0.1)"
                          : "#fffbeb",
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  fullWidth
                  variant="contained"
                  sx={{
                    borderRadius: "12px",
                    py: 1.2,
                    textTransform: "none",
                    fontWeight: 500,
                    background:
                      "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                    color: "#ffffff",
                    boxShadow: "0 10px 15px -3px rgba(245, 158, 11, 0.25)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                      transform: "scale(1.02)",
                      boxShadow: "0 20px 25px -5px rgba(245, 158, 11, 0.3)",
                    },
                    "&:disabled": {
                      opacity: 0.7,
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} sx={{ color: "#ffffff" }} />
                  ) : selectedRole ? (
                    "Update Permissions"
                  ) : (
                    "Assign Permissions"
                  )}
                </Button>
              </Box>
            </form>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default AddRoleAccessModal;
